import { useCallback, useEffect } from "react";
import { Location, CreateLocation, UpdateLocation } from "../types/models/Location.d";
import { UpdateEntityLocation } from "../types/models/Location";
import { useGetLocationListQuery, useInsertLocationMutation, useUpdateLocationMutation, useDeleteLocationMutation, } from "../apis/Location.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useLocation(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertLocationMutation, { 
        isLoading: insertLocationLoading, 
        isSuccess: insertLocationIsSuccess,
        isError: insertLocationIsError,
        error: insertLocationError,
    }] = useInsertLocationMutation()
    const [updateLocationMutation, { 
        isLoading: updateLocationLoading,
        isSuccess: updateLocationIsSuccess,
        isError: updateLocationIsError,
        error: updateLocationError,
    }] = useUpdateLocationMutation()
    const [deleteLocationMutation, { 
        isLoading: deleteLocationLoading,
        isSuccess: deleteLocationIsSuccess,
        isError: deleteLocationIsError,
        error: deleteLocationError,
    }] = useDeleteLocationMutation()

    const loading =
        insertLocationLoading ||
        updateLocationLoading ||
        deleteLocationLoading ||
        false

    const insertLocation = useCallback(async (location: CreateLocation, redirectPath: string|undefined) => {
        try {
            const data = await insertLocationMutation(location).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertLocationMutation, navigate])
    
    useEffect(() => {
        if (insertLocationIsSuccess) {
            openSuccessAlert('Location created')
        } else if (insertLocationIsError) {
            console.log("add error", insertLocationError)
            openFailAlert(t`Location add error: ` + insertLocationError?.data?.message)
        }
    }, [
        insertLocationIsSuccess,
        insertLocationIsError,
        insertLocationError
    ]);

    const updateLocation = useCallback(async (location: UpdateEntityLocation, redirectPath: string|undefined) => {
        try {
            const data = await updateLocationMutation(location).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateLocationMutation, navigate])
    
    useEffect(() => {
        if (updateLocationIsSuccess) {
            openSuccessAlert(t`Location updated`)
        } else if (updateLocationIsError) {
            console.log("update error", updateLocationError)
            openFailAlert(t`Location update error: ` + updateLocationError?.data?.message)
        }
    }, [
        updateLocationIsSuccess,
        updateLocationIsError,
        updateLocationError
    ]);

    const deleteLocation = useCallback((location: Location, redirectPath: string|undefined) => {
        openDialog(async () => {
            location = {
                ...location,
            }
            try {
                const { data } = await deleteLocationMutation(location);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteLocationMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteLocationIsSuccess) {
            openSuccessAlert(t`Location deleted`)
        } else if (deleteLocationIsError) {
            console.log("delete error", deleteLocationError)
            openFailAlert(t`Location delete error: ` + deleteLocationError?.data?.message)
        }
    }, [
        deleteLocationIsSuccess,
        deleteLocationIsError,
        deleteLocationError
    ]);

    return {
        loading,
        insertLocation,
        updateLocation,
        deleteLocation
    }
}
